'use client';

import {
  type ElementType,
  type ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  useMemo,
} from 'react';

import { css, cx } from '@/styled-system/css';

import { FormFieldContext } from '../form-field/context/form-field.context';
import type { FormFieldContextInterface } from '../form-field/types/form-field.types';
import { FieldSetContext } from './context/field-set.context';
import { fieldSetStyles } from './styles/field-set.styles';
import type { FieldSetContextInterface, FieldSetPropsInterface } from './types/field-set.types';

/**
 * @related [`FormField`, `LabeledControl`, `Tooltip`]
 * @subcomponents [`Legend`]
 * @since 8.0.0
 */

export const FieldSet = forwardRef<HTMLFieldSetElement | HTMLDivElement, FieldSetPropsInterface>(
  (props: FieldSetPropsInterface, forwardedRef) => {
    const {
      children,
      className,
      css: cssProp,
      description,
      disabled = false,
      error = false,
      legend,
      required = false,
      requiredText = 'asterisk',
      optional = false,
      size,
      tooltip,
      ...rest
    } = props;

    const styles = css(fieldSetStyles, cssProp);

    const descriptionId = useId();
    const legendId = useId();

    const legendElement = isValidElement<{ children?: ReactNode }>(legend) ? legend : null;
    const hasLegendTooltip = Boolean(tooltip) && legendElement !== null;

    const formFieldContextValue = useMemo<FormFieldContextInterface>(
      () => ({
        error,
        disabled,
        ...(description ? { descriptionId } : {}),
        optional,
        required,
        requiredText,
      }),
      [error, disabled, description, descriptionId, optional, required, requiredText],
    );

    const fieldSetContextValue = useMemo<FieldSetContextInterface>(
      () => ({
        size,
      }),
      [size],
    );

    const Component = (legend ? 'fieldset' : 'div') as ElementType;

    /*
     * `<fieldset>` already has an implicit `role="group"`, so setting the
     * role explicitly is redundant. The `<div>` fallback
     * still needs the role to be announced as a group.
     */
    const role = legend ? undefined : 'group';

    /*
     * A `<fieldset>` applies its own `display` to an anonymous content box and
     * lifts the rendered legend out of it, so a sibling can never share the
     * legend's line. Chromium and Firefox let the legend opt out of that
     * treatment via `float`, but Safari does not, so the tooltip is injected
     * into the legend instead.
     *
     * The original legend content is wrapped so `aria-labelledby` can name the
     * group from the text alone. Without it the trigger's own label ("Help")
     * would be appended to the group's accessible name.
     */
    const legendContent =
      legendElement && tooltip
        ? cloneElement(
            legendElement,
            undefined,
            <span id={legendId}>{legendElement.props.children}</span>,
            tooltip,
          )
        : legend;

    return (
      <FormFieldContext.Provider value={formFieldContextValue}>
        <FieldSetContext.Provider value={fieldSetContextValue}>
          <Component
            ref={forwardedRef as any}
            aria-labelledby={hasLegendTooltip ? legendId : undefined}
            className={cx('c11n', 'c11n_field-set', styles, className)}
            data-c11n-component="FieldSet"
            data-c11n-version={__C11N_VERSION__}
            role={role}
            {...rest}
          >
            {legendContent}
            {children}
            {description}
          </Component>
        </FieldSetContext.Provider>
      </FormFieldContext.Provider>
    );
  },
);

FieldSet.displayName = 'FieldSet';
